// Store Performance Service - Provides store revenue and conversion metrics
import moment from "moment-timezone";

import { formatStoreDateKey, getBrowserTimezone } from "@/utils/storeTime";

export interface StoreConversionData {
  conversionRate: number;
  previousConversionRate: number;
  totalVisits: number;
  totalPurchases: number;
  period: string;
}

export interface AnalyticsCardData {
  value: string;
  previousValue: string | null;
  subtitle: string;
  isPositive: boolean;
  pillText: string | null;
  rawValue?: number | null;
  /** Raw value used exclusively for period-over-period trends when it differs from rawValue. */
  trendRawValue?: number | null;
  loading?: boolean;
  salesWindow?: Pick<SalesQueryWindow, "startDate" | "endDate" | "isLimited">;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export type TrendComparisonWindow = "day_before" | "week_before" | "month_before" | "year_before";

export interface SalesQueryWindow {
  startDate: string;
  endDate: string;
  isAvailable: boolean;
  isLimited: boolean;
}

export class StorePerformanceService {
  private static instance: StorePerformanceService;

  constructor() {}

  static getInstance(): StorePerformanceService {
    if (!StorePerformanceService.instance) {
      StorePerformanceService.instance = new StorePerformanceService();
    }
    return StorePerformanceService.instance;
  }

  formatDate = (date: Date | undefined, timezone: string = getBrowserTimezone()): string => {
    if (!date) return "";

    return formatStoreDateKey(date, timezone);
  };

  getSalesQueryWindow(
    dateRange: DateRange,
    latestSalesDate: Date | null | undefined,
    timezone: string = getBrowserTimezone(),
  ): SalesQueryWindow {
    const startDate = this.formatDate(dateRange.start, timezone);
    const selectedEndDate = this.formatDate(dateRange.end, timezone);
    const latestSalesDateKey = latestSalesDate ? this.formatDate(latestSalesDate, timezone) : null;

    if (!latestSalesDateKey || latestSalesDateKey >= selectedEndDate) {
      return { startDate, endDate: selectedEndDate, isAvailable: true, isLimited: false };
    }

    if (latestSalesDateKey < startDate) {
      return { startDate, endDate: selectedEndDate, isAvailable: false, isLimited: true };
    }

    return { startDate, endDate: latestSalesDateKey, isAvailable: true, isLimited: true };
  }

  /**
   * Shortens the already weekday-aligned prior range to the same number of
   * browser-local calendar days as the current sales window. This prevents a
   * delayed sales feed from comparing a partial current period with a complete
   * prior one.
   */
  getAlignedSalesComparisonWindow(
    comparisonRange: DateRange,
    currentSalesWindow: SalesQueryWindow,
    timezone: string = getBrowserTimezone(),
  ): SalesQueryWindow {
    const startDate = this.formatDate(comparisonRange.start, timezone);

    if (!currentSalesWindow.isAvailable) {
      return {
        startDate,
        endDate: this.formatDate(comparisonRange.end, timezone),
        isAvailable: false,
        isLimited: currentSalesWindow.isLimited,
      };
    }

    const daySpan = moment
      .tz(currentSalesWindow.endDate, "YYYY-MM-DD", timezone)
      .diff(moment.tz(currentSalesWindow.startDate, "YYYY-MM-DD", timezone), "days");

    return {
      startDate,
      endDate: moment
        .tz(startDate, "YYYY-MM-DD", timezone)
        .add(daySpan, "days")
        .format("YYYY-MM-DD"),
      isAvailable: true,
      isLimited: currentSalesWindow.isLimited,
    };
  }

  setToMonday(date: Date, timezone: string = getBrowserTimezone()): Date {
    return moment(date).tz(timezone).isoWeekday(1).startOf("day").toDate();
  }

  getWeekRange(today: Date = new Date(), timezone: string = getBrowserTimezone()): DateRange {
    const zonedToday = moment(today).tz(timezone);
    const start = zonedToday.clone().isoWeekday(1).startOf("day").toDate();
    const end = zonedToday.clone().startOf("day").toDate();

    return { start, end };
  }

  getDailyRange(today: Date = new Date(), timezone: string = getBrowserTimezone()): DateRange {
    const zonedToday = moment(today).tz(timezone);
    const start = zonedToday.clone().startOf("day").toDate();
    const end = zonedToday.toDate();

    return {
      start,
      end,
    };
  }

  getYesterdayRange(today: Date = new Date(), timezone: string = getBrowserTimezone()): DateRange {
    const zonedYesterday = moment(today).tz(timezone).subtract(1, "day").startOf("day");
    const start = zonedYesterday.toDate();
    const end = zonedYesterday.clone().toDate();

    return { start, end };
  }

  getLast4WeeksRange(today: Date = new Date(), timezone: string = getBrowserTimezone()): DateRange {
    // The four complete Monday–Sunday weeks immediately preceding the current week.
    const currentWeekStart = moment(today).tz(timezone).isoWeekday(1).startOf("day");
    const start = currentWeekStart.clone().subtract(4, "weeks").toDate();
    const end = currentWeekStart.clone().subtract(1, "day").toDate();
    return { start, end };
  }

  getLastWeekendRange(
    today: Date = new Date(),
    timezone: string = getBrowserTimezone(),
  ): DateRange {
    const base = moment(today).tz(timezone).startOf("day");
    const dow = base.day(); // 0=Sun..6=Sat
    let offset = (dow - 6 + 7) % 7; // days since last Saturday
    if (dow === 6 || dow === 0) {
      offset += 7;
    }
    const start = base.clone().subtract(offset, "days").toDate(); // Saturday 00:00
    const end = base.clone().subtract(offset, "days").add(1, "day").toDate(); // Sunday 00:00
    return { start, end };
  }

  getComparisonRange(
    period: string,
    currentRange: DateRange,
    timezone: string = getBrowserTimezone(),
  ): DateRange {
    const start = moment(currentRange.start).tz(timezone);
    const end = moment(currentRange.end).tz(timezone);

    if (period === "today" || period === "yesterday") {
      return {
        start: start.clone().subtract(1, "week").toDate(),
        end: end.clone().subtract(1, "week").toDate(),
      };
    }

    if (period === "last_4_weeks") {
      return { start: start.subtract(4, "weeks").toDate(), end: end.subtract(4, "weeks").toDate() };
    }

    if (period === "week_to_date" || period === "last_weekend") {
      return { start: start.subtract(1, "week").toDate(), end: end.subtract(1, "week").toDate() };
    }

    // Custom ranges compare to the same browser-local weekdays in an earlier
    // whole-week block. Shift enough complete weeks for the comparison to end
    // before the current range begins.
    const calendarDaySpan = end.clone().startOf("day").diff(start.clone().startOf("day"), "days");
    const weeksToShift = Math.max(1, Math.ceil((calendarDaySpan + 1) / 7));
    return {
      start: start.clone().subtract(weeksToShift, "weeks").toDate(),
      end: end.clone().subtract(weeksToShift, "weeks").toDate(),
    };
  }

  getTrendComparisonRange(
    currentRange: DateRange,
    comparisonWindow: TrendComparisonWindow,
    timezone: string = getBrowserTimezone(),
    anchorToWeekdays: boolean = true,
  ): DateRange {
    const start = moment(currentRange.start).tz(timezone).startOf("day");
    const end = moment(currentRange.end).tz(timezone).startOf("day");

    if (comparisonWindow === "day_before") {
      return {
        start: start.clone().subtract(1, "day").toDate(),
        end: end.clone().subtract(1, "day").toDate(),
      };
    }

    if (comparisonWindow === "week_before") {
      return {
        start: start.clone().subtract(1, "week").toDate(),
        end: end.clone().subtract(1, "week").toDate(),
      };
    }

    const daySpan = end.diff(start, "days");

    if (!anchorToWeekdays) {
      const comparisonStart = start
        .clone()
        .subtract(1, comparisonWindow === "month_before" ? "month" : "year");

      return {
        start: comparisonStart.toDate(),
        end: comparisonStart.clone().add(daySpan, "days").toDate(),
      };
    }

    const targetMonth = start
      .clone()
      .subtract(1, comparisonWindow === "month_before" ? "month" : "year")
      .startOf("month");
    const targetMonthEnd = targetMonth.clone().endOf("month");
    const weekday = start.isoWeekday();
    const weekdayOccurrence = Math.floor((start.date() - 1) / 7) + 1;
    const firstMatchingWeekday = targetMonth
      .clone()
      .add((weekday - targetMonth.isoWeekday() + 7) % 7, "days");
    const alignedStart = firstMatchingWeekday.clone().add(weekdayOccurrence - 1, "weeks");
    const comparisonStart = alignedStart.isSame(targetMonth, "month")
      ? alignedStart
      : targetMonthEnd.clone().subtract((targetMonthEnd.isoWeekday() - weekday + 7) % 7, "days");
    return {
      start: comparisonStart.toDate(),
      end: comparisonStart.clone().add(daySpan, "days").toDate(),
    };
  }

  // Get store conversion data
  getStoreConversion(): StoreConversionData {
    // Demo data - replace with actual API call
    const totalVisits = 8450;
    const totalPurchases = 2134;
    const currentConversionRate = (totalPurchases / totalVisits) * 100;
    const previousConversionRate = 23.8;

    return {
      conversionRate: Math.round(currentConversionRate * 100) / 100,
      previousConversionRate: previousConversionRate,
      totalVisits: totalVisits,
      totalPurchases: totalPurchases,
      period: "This Month",
    };
  }
}

export const storePerformanceService = StorePerformanceService.getInstance();
